import { motion } from "framer-motion";
import { Shield, Zap, Eye, Bug, Brain, Globe } from "lucide-react";

const capabilities = [
  {
    icon: Shield,
    title: "Credential Stuffing",
    description: "Detects automated login attempts using stolen credential databases.",
    stat: "34%",
    statLabel: "of login traffic is bots",
  },
  {
    icon: Eye,
    title: "Content Scrapers",
    description: "Identifies bots copying your content, pricing, or product data.",
    stat: "60K",
    statLabel: "scraper requests blocked/day",
  },
  {
    icon: Bug,
    title: "Ad Fraud Bots",
    description: "Catches bots inflating your ad impressions and draining budgets.",
    stat: "22%",
    statLabel: "of ad clicks are fraudulent",
  },
  {
    icon: Brain,
    title: "AI Training Crawlers",
    description: "Detects LLM training bots harvesting your site without permission.",
    stat: "3x",
    statLabel: "increase since 2024",
  },
  {
    icon: Globe,
    title: "DDoS Layer 7",
    description: "Identifies application-layer attacks disguised as normal browsing.",
    stat: "<50ms",
    statLabel: "detection latency",
  },
  {
    icon: Zap,
    title: "Account Takeover",
    description: "Flags automated account enumeration and brute-force attempts.",
    stat: "99.2%",
    statLabel: "detection accuracy",
  },
];

const WhatWeDetect = () => {
  return (
    <section className="relative py-16 md:py-24 px-6">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <p className="text-[10px] font-mono text-primary uppercase tracking-[0.3em] mb-4">
            What We Detect
          </p>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl text-foreground tracking-tight">
            Every bot. Every attack vector.
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mt-4 max-w-2xl mx-auto font-mono">
            BotSweep fingerprints and classifies automated traffic across all known attack patterns.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {capabilities.map((cap, i) => (
            <motion.div
              key={cap.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="group relative rounded-xl border border-border bg-card/50 p-6 hover:border-primary/30 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <cap.icon className="w-5 h-5 text-primary" />
                </div>
                <div className="text-right">
                  <span className="text-xl font-data font-bold text-foreground">{cap.stat}</span>
                  <p className="text-[9px] font-mono text-muted-foreground">{cap.statLabel}</p>
                </div>
              </div>
              <h3 className="font-display font-bold text-sm text-foreground mb-1">{cap.title}</h3>
              <p className="text-[11px] font-mono text-muted-foreground leading-relaxed">{cap.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhatWeDetect;
